import * as React from "react"

import {
  FormFieldLabel,
  type FormFieldProps,
} from "@/components/forms/FormField"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
} from "@/components/ui/field"
import { cn } from "cn"

export type FormCheckboxProps = FormFieldProps &
  Omit<
    React.ComponentProps<typeof Checkbox>,
    "className" | "onCheckedChange" | "checked"
  > & {
    checked?: boolean
    /**
     * Called with the boolean checked state. Matches Base UI / shadcn Checkbox
     * (`onCheckedChange`), not a native input event.
     */
    onChange?: (checked: boolean) => void
    inputClassName?: string
  }

/**
 * Labeled checkbox with description / error associations and required marker.
 *
 * Layout matches shadcn’s checkbox field: checkbox beside label, with
 * description/error stacked under the label via `FieldContent`.
 *
 * Drive with `checked` / `onChange(checked)` (react-hook-form `Controller` or
 * local state). Unlike {@link FormInput}, `onChange` receives the boolean
 * value directly.
 *
 * For several related checkboxes under one title, wrap them in
 * {@link FormCheckboxGroup}.
 *
 * @example RHF
 * ```tsx
 * <Controller
 *   control={control}
 *   name="hideBooker"
 *   render={({ field }) => (
 *     <FormCheckbox
 *       label="Hide From Booker Selection?"
 *       description="Prevents this booker from appearing in searches."
 *       checked={Boolean(field.value)}
 *       onChange={field.onChange}
 *     />
 *   )}
 * />
 * ```
 *
 * @example Controlled
 * ```tsx
 * <FormCheckbox
 *   label="Requires Invoice Notes"
 *   checked={requiresNotes}
 *   onChange={setRequiresNotes}
 * />
 * ```
 */
export function FormCheckbox({
  label,
  required,
  description,
  error,
  fieldClassName,
  inputClassName,
  id,
  ref,
  disabled,
  checked,
  onChange,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedByProp,
  ...checkboxProps
}: FormCheckboxProps) {
  const generatedId = React.useId()
  const checkboxId = id ?? generatedId
  const invalid =
    Boolean(error) ||
    (ariaInvalid != null && ariaInvalid !== false && ariaInvalid !== "false")

  const descriptionId = description ? `${checkboxId}-description` : undefined
  const errorId = error ? `${checkboxId}-error` : undefined
  const ariaDescribedBy =
    [descriptionId, errorId, ariaDescribedByProp].filter(Boolean).join(" ") ||
    undefined

  return (
    <Field
      orientation="horizontal"
      className={cn(fieldClassName)}
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
    >
      <Checkbox
        {...checkboxProps}
        ref={ref}
        id={checkboxId}
        checked={checked}
        disabled={disabled}
        required={required}
        aria-invalid={ariaInvalid ?? (error ? true : undefined)}
        aria-describedby={ariaDescribedBy}
        className={inputClassName}
        onCheckedChange={(next) => onChange?.(next === true)}
      />
      <FieldContent>
        <FormFieldLabel
          htmlFor={checkboxId}
          label={label}
          required={required}
        />
        {description ? (
          <FieldDescription id={descriptionId}>{description}</FieldDescription>
        ) : null}
        {error ? <FieldError id={errorId}>{error}</FieldError> : null}
      </FieldContent>
    </Field>
  )
}

export type FormCheckboxGroupProps = FormFieldProps & {
  disabled?: boolean
  invalid?: boolean
  children: React.ReactNode
}

/**
 * Chrome for a list of related checkboxes (title, description, error, disabled).
 * Does not own checkbox values — each child {@link FormCheckbox} keeps its own
 * `checked` / `onChange` (typically via separate react-hook-form Controllers).
 *
 * @example
 * ```tsx
 * <FormCheckboxGroup
 *   label="Approval Requirements"
 *   description={!adminMode ? "Editable by admins only" : undefined}
 *   disabled={!adminMode}
 * >
 *   <Controller
 *     control={control}
 *     name="requiresInvoiceNotes"
 *     render={({ field }) => (
 *       <FormCheckbox
 *         label="Requires Invoice Notes"
 *         checked={Boolean(field.value)}
 *         onChange={field.onChange}
 *         disabled={!adminMode}
 *       />
 *     )}
 *   />
 * </FormCheckboxGroup>
 * ```
 */
export function FormCheckboxGroup({
  label,
  required,
  description,
  error,
  fieldClassName,
  disabled,
  invalid,
  children,
}: FormCheckboxGroupProps) {
  const generatedId = React.useId()
  const descriptionId = description ? `${generatedId}-description` : undefined
  const errorId = error ? `${generatedId}-error` : undefined
  const ariaDescribedBy =
    [descriptionId, errorId].filter(Boolean).join(" ") || undefined

  return (
    <Field
      className={cn("gap-1", fieldClassName)}
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
    >
      <FormFieldLabel label={label} required={required} />
      <FieldGroup
        data-slot="checkbox-group"
        className="gap-3"
        role="group"
        aria-describedby={ariaDescribedBy}
      >
        {disabled ? (
          <fieldset disabled className="space-y-3 border-0 p-0">
            {children}
          </fieldset>
        ) : (
          children
        )}
      </FieldGroup>
      {description ? (
        <FieldDescription id={descriptionId}>{description}</FieldDescription>
      ) : null}
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </Field>
  )
}
